import React, { useState, useEffect, useRef, useCallback } from "react";
import FeatureEnquiryModal from "../components/FeatureEnquiryModal";
import ServiceModal from "../components/ServiceModal";
import { ProductIds } from "../components/FeaturedCardsCarousel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// Import images
import LettingsImg from "../../images/product_bar/Lettings.png";
import ListingsImg from "../../images/product_bar/Listing.png";
import RefurbishmentImg from "../../images/product_bar/Refurbishment.png";
import BrokerageImg from "../../images/product_bar/Brokerage.png";
import LegalsImg from "../../images/product_bar/Legals.png";
import CompanysetUpImg from "../../images/product_bar/CompanySetup.png";
import FurnishingImg from "../../images/product_bar/Furnishing.png";
import RemovalsImg from "../../images/product_bar/Removals.png";
import SurveyingImg from "../../images/product_bar/Surveys.png";
import HousingAssociationImg from "../../images/product_bar/HousingAssociation.png";
import HomeInsuranceImg from "../../images/product_bar/HomeInsurance.png";
import DigitalPlusImg from "../../images/product_bar/Digital+Marketing.png";

interface PropertyNavigationProps {
  currentPage: string;
}

interface NavItemProps {
  image?: string;
  icon?: IconDefinition;
  text: string;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const NavItem: React.FC<NavItemProps> = ({ image, icon, text, onClick, className, style }) => {

  const itemContent = (
    <>
      {image ? (
        <img 
          src={image} 
          alt={text}
          className="w-12 h-12 mb-2 object-contain" 
        />
      ) : icon ? (
        <FontAwesomeIcon 
          icon={icon} 
          className="w-6 h-6 mb-2" 
        />
      ) : null}
      <span className="text-center text-xs">{text}</span>
    </>
  );

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center text-xs px-4 py-2 rounded-lg transition-all duration-300 ease-in-out whitespace-nowrap text-gray-700 hover:bg-gray-100 ${className || ''}`}
      style={{ flexShrink: 0, ...style }}
    >
      {itemContent}
    </button>
  );
};

const PropertyNavigationBar: React.FC<PropertyNavigationProps> = ({ currentPage }) => {
  const [index, setIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(5);
  const [itemWidth, setItemWidth] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Modal state
  const [modalContent, setModalContent] = useState<any>(null);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  // Service modal definitions
  const serviceModals = {
    lettings: {
      title: "Lettings",
      description: "Leave your property in trusted hands! With Our Efficient National Lettings Agency, our experienced team works tirelessly to <strong>protect your investment and maximise your rental income",
      productId: ProductIds.lettings
    },
    listings: {
      title: "Listings",
      description: "<strong>Selling your property?</strong> Gain immediate access to a vast network of active home buyers and property investors through our off-market estate agency.",
      productId: ProductIds.listings
    },
    digitalPlus: {
      title: "Digital Marketing",
      description: "We offer digital marketing services for property sourcers and sellers, designed to showcase investment opportunities and attract serious buyers. Services include high-quality brand positioning, listing management across key platforms, social campaigns, and targeted online advertising to maximise visibility and drive investor interest.",
      productId: ProductIds.digitalPlus
    },
    refurbishment: {
      title: "Refurbishment",
      description: "We'll Repair, Improve and Maintain Your Property. We specialise in property transformation and repairs.",
      productId: ProductIds.refurbishment
    },
    brokerage: {
      title: "Brokerage",
      description: "We offer tailored mortgage solutions with a friendly, professional touch. Whether you're a first-time buyer or expanding your property portfolio.",
      productId: ProductIds.brokerage
    },
    legals: {
      title: "Legals",
      description: "Benefit from low-cost conveyancing fees through our panel of solicitors. We offer a range of services designed to make your property transactions smooth and hassle-free.",
      productId: ProductIds.legals
    },
    companySetup: {
      title: "Company Setup",
      description: "We offer a complete company setup service for property investors, including fast UK limited company incorporation, legal documentation, a registered office address, and business bank account setup. Ongoing services cover bookkeeping, tax filings, and compliance—ensuring each company is optimally structured to own a single buy-to-let property efficiently and securely.",
      productId: ProductIds.companySetup
    },
    furnishing: {
      title: "Furnishing",
      description: "We offer a comprehensive furnishing service tailored to investment properties, delivering stylish, durable furniture packages that meet rental market expectations. Our service ensures properties are move-in ready for tenants, helping investors maximise rental value and minimise void periods.",
      productId: ProductIds.furnishing
    },
    removals: {
      title: "Removals",
      description: "We offer reliable and efficient removals services for investment properties, ensuring seamless transitions whether you're preparing a property for sale, staging, or tenant move-in. Our team handles everything from packing and transportation to clearance and setup, saving you time and hassle.",
      productId: ProductIds.removals
    },
    surveying: {
      title: "Surveying",
      description: "We provide professional property surveys tailored for investment properties, giving buyers clear insights into a property's condition and potential risks. Our services include RICS Home Surveys, building condition reports, and snagging inspections—ensuring informed decisions, reduced risk, and smoother transactions for investors.",
      productId: ProductIds.surveying
    },
    housingAssociation: {
      title: "Housing Association",
      description: "Receive guaranteed income on your property by leasing the property to one of our housing association partners. For a minimum for a five year term. The housing providers we work with are all tier one and tier two, insuring minimal risk to you, the landlord. We’ll take care of internal maintenance and management for an easy hands off investment.",
      productId: ProductIds.housingAssociation
    },
    homeInsurance: {
      title: "Home Insurance",
      description: "We provide tailored home insurance solutions for investment properties, offering comprehensive coverage to protect against risks such as damage, theft, and loss of rental income. Our policies are designed to meet the specific needs of property investors, ensuring peace of mind and financial security.",
      productId: ProductIds.homeInsurance
    }
  };
  const navItems = [
    { image: LettingsImg, text: "Lettings", onClick: () => setModalContent(serviceModals.lettings) },
    { image: ListingsImg, text: "Listings", onClick: () => setModalContent(serviceModals.listings) },
    { image: DigitalPlusImg, text: "Marketing", onClick: () => setModalContent(serviceModals.digitalPlus) },
    { image: RefurbishmentImg, text: "Refurbishment", onClick: () => setModalContent(serviceModals.refurbishment) },
    { image: BrokerageImg, text: "Brokerage", onClick: () => setModalContent(serviceModals.brokerage) },
    { image: LegalsImg, text: "Legals", onClick: () => setModalContent(serviceModals.legals) },
    { image: CompanysetUpImg, text: "Company Setup", onClick: () => setModalContent(serviceModals.companySetup) },
    { image: FurnishingImg, text: "Furnishing", onClick: () => setModalContent(serviceModals.furnishing) },
    { image: RemovalsImg, text: "Removals", onClick: () => setModalContent(serviceModals.removals) },
    { image: SurveyingImg, text: "Surveying", onClick: () => setModalContent(serviceModals.surveying) },
    { image: HousingAssociationImg, text: "H.A", onClick: () => setModalContent(serviceModals.housingAssociation) },
    { image: HomeInsuranceImg, text: "Home Insurance", onClick: () => setModalContent(serviceModals.homeInsurance) }
  ];

  const totalItems = navItems.length;
  const displayList = [...navItems, ...navItems, ...navItems];

  // Calculate items per view on resize
  useEffect(() => {
    const updateItems = () => {
      const width = window.innerWidth;
      if (width < 640) setItemsPerView(3);
      else if (width < 768) setItemsPerView(4);
      else if (width < 1024) setItemsPerView(5);
      else if (width < 1280) setItemsPerView(6);
      else setItemsPerView(7);
    };
    updateItems();
    window.addEventListener('resize', updateItems);
    return () => window.removeEventListener('resize', updateItems);
  }, []);

  // Calculate item width
  useEffect(() => {
    const resize = () => {
      if (wrapperRef.current) {
        setItemWidth(wrapperRef.current.offsetWidth / itemsPerView);
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [itemsPerView]);

  // Handle next
  const handleNext = useCallback(() => {
    setIndex(i => {
      if (i >= totalItems * 2) {
        return totalItems;
      }
      return i + 1;
    });
  }, [totalItems]);

  // Handle prev with wrap
  const handlePrev = useCallback(() => {
    setIndex(i => {
      if (i <= 0) {
        return totalItems;
      }
      return i - 1;
    });
  }, [totalItems]);

  // Auto-scroll
  useEffect(() => {
    const id = setInterval(handleNext, 3000);
    return () => clearInterval(id);
  }, [handleNext]);

  // Enquire handler
  const handleEnquire = (productId: string) => {
    setSelectedProductId(productId);
    setIsFeatureModalOpen(true);
    setModalContent(null);
  };

  return (
    <div className="max-w-7xl mx-auto my-8 pt-8 px-4">
      <div ref={wrapperRef} className="overflow-hidden relative">
        <div
          ref={trackRef}
          className="flex"
          style={{
            width: `${displayList.length * itemWidth}px`,
            transform: `translateX(-${index * itemWidth}px)`,
            transition: 'transform 0.5s ease-in-out'
          }}
        >
          {displayList.map((item, i) => (
            <NavItem
              key={i}
              image={item.image}
              text={item.text}
              onClick={item.onClick}
              className="flex-shrink-0"
              style={{ width: `${itemWidth}px` }}
            />
          ))}
        </div>

        {/* Prev & Next Buttons */}
        <button onClick={handlePrev} className="absolute left-0 top-1/2 transform -translate-y-1/2 p-2 rounded-full hover:bg-gray-100 transition-colors">
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
        <button onClick={handleNext} className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 rounded-full hover:bg-gray-100 transition-colors">
          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
        </button>
      </div>

      {/* Modals */}
      {modalContent && (
        <ServiceModal
          isOpen={!!modalContent}
          onClose={() => setModalContent(null)}
          content={modalContent}
          onEnquire={handleEnquire}
        />
      )}
      <FeatureEnquiryModal
        isOpen={isFeatureModalOpen}
        onClose={() => setIsFeatureModalOpen(false)}
        productId={selectedProductId}
      />
    </div>
  );
};

export default PropertyNavigationBar;
